import { useMemo, useState, useEffect, useCallback } from 'react';

import * as cookie from '~/App/helpers/cookie';
import { getClientId } from '~/App/helpers/ga-helper';
import { pushGTMUserId, GtmDetails } from '~/App/helpers/gtm-helper';

import { gtmDetails, gtmCustomDetails } from '~/App/config/gtmDetails';
import { ProductValues } from '.';

export type AnalyticsValues = {
  productOptions: {
    campaignActivityId?: string | number;
  };
  clientOptions: {
    clientId: string | null;
    gclId?: string | null;
    userId: string | null;
  };
  gtm: GtmDetails;
  handlers: {
    setUserId: (value: string | null) => void;
    setGtmVariant: (value?: string) => void;
    setCampaignActivityId: (value?: number) => void;
    unsetCampaignActivityId: () => void;
    setGtm: (value: GtmDetails) => void;
  };
};

type InputProps = {
  values: ProductValues & {
    productOptions?: {
      campaignActivityId?: number | string | null;
    } & Record<string, unknown>;
    gtm?: {
      formName?: string | undefined;
      variant?: string;
    };
  };
  formContent?: {
    gtmId?: string;
  };
};

export function useAnalyticsValues<T extends InputProps>({
  values,
  formContent
}: T) {
  const [userId, setUserId] = useState<string | null>(null);
  const [campaignActivityId, setCampaignActivityId] = useState(
    values.productOptions?.campaignActivityId || undefined
  );

  const productId = useMemo(() => values.product?.id || 31, [values]);

  const gtmDefaultValue: GtmDetails = useMemo<GtmDetails>(() => {
    const gtmId = formContent?.gtmId;

    if (gtmId && gtmCustomDetails[gtmId]) {
      return {
        ...values.gtm,
        ...gtmCustomDetails[gtmId],
        formName: values.gtm?.formName,
        variant: values.gtm?.variant
      };
    }

    if (gtmDetails[productId]) {
      return {
        ...values.gtm,
        ...gtmDetails[productId],
        formName: values.gtm?.formName,
        variant: values.gtm?.variant
      };
    }

    return {
      name: '',
      brand: '',
      category: '',
      ...values.gtm
    };
  }, [productId, formContent, values.gtm]);

  const [gtm, setGtm] = useState<GtmDetails>({
    ...gtmDefaultValue
  });

  const setGtmVariant = useCallback(
    (variant: string | undefined) => setGtm({ ...gtm, variant }),
    [gtm]
  );

  const gclId = useMemo(() => cookie.get('gclid'), []);
  const clientId = useMemo(getClientId, []);

  const unsetCampaignActivityId = useCallback(
    () => setCampaignActivityId(undefined),
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!userId) return;

    pushGTMUserId(userId);
  }, [userId]);

  return useMemo<AnalyticsValues & T['values']>(
    () => ({
      ...values,
      gtm,
      clientOptions: {
        clientId,
        gclId,
        userId
      },
      productOptions: {
        ...values.productOptions,
        campaignActivityId
      },
      handlers: {
        ...values.handlers,
        setUserId,
        setGtmVariant,
        setGtm,
        setCampaignActivityId,
        unsetCampaignActivityId
      }
    }),
    [
      campaignActivityId,
      clientId,
      gclId,
      gtm,
      unsetCampaignActivityId,
      userId,
      values
    ]
  );
}
